import { PayloadAction } from '@reduxjs/toolkit';

import {
  CheckStatus,
  PolingStatus,
  PreprocessingStatus,
  UploadFormData,
} from '@components/SiteUserUpload/types/uploadTypes';
import { UploadFileTypeEnum } from '@dynamika-shared/types/inventory';
import { CreateVisit } from 'src/store-redux/siteUpload/siteUploadStore.types';

export enum UploadVisitType {
  REGULAR = 'REGULAR',
  UNSCHEDULED = 'UNSCHEDULED',
  EARLY_TERMINATED = 'EARLY TERMINATED',
}

export type VisitResponse = {
  id: string;
  patientId: string;
  visitId?: string;
  visitDefinitionId?: string | null;
  siteId: string;
  clinicalTrialId: string;
  earlyTerminated: boolean;
  createUnscheduled: boolean;
  visitName: string;
  fileType?: UploadFileTypeEnum;
};

export type VerificationStepsItem = {
  id: string;
  status: CheckStatus;
  type: string;
  title: string;
  message: string;
  comment: string;
  index: number;
};

export type FormState = {
  [key: string]: string | boolean;
};

export type PolingResponse = {
  preprocessingStatus: PreprocessingStatus;
  status: PolingStatus;
  verificationSteps: VerificationStepsItem[];
};

export enum UploadStatus {
  INITIAL_VIEW = 'INITIAL_VIEW',
  FILE_UPLOAD_IN_PROGRESS = 'FILE_UPLOAD_IN_PROGRESS',
  FILE_UPLOAD_SUCCESS = 'FILE_UPLOAD_SUCCESS',
  FILE_UPLOAD_ERROR = 'FILE_UPLOAD_ERROR',

  FILE_FINALIZE_INIT = 'FILE_FINALIZE_INIT',
  FILE_FINALIZE_ERROR = 'FILE_FINALIZE_ERROR',

  FILE_POLING_SUCCESS = 'FILE_POLING_SUCCESS',
  FILE_POLING_WARNING = 'FILE_POLING_WARNING',
  FILE_POLING_ERROR = 'FILE_POLING_ERROR',
  FILE_DICOM_ERROR = 'FILE_DICOM_ERROR',

  FINALIZE_UPLOAD = 'FINALIZE_UPLOAD',
}

export type VisitProps = {
  value: string;
  id?: string;
  visitName?: string;
};

export type SiteUploadState = {
  data: VisitResponse | null;
  selectedSchema: UploadFormData | null;
  schemas: UploadFormData[];
  signedUrlResponse: { signedUrl: string } | null;
  edtfData: null | FormState;
  status: null | UploadStatus;
  polingData: null | undefined | PolingResponse;
  loading: boolean;
  error?: Error;
  shouldContinuePolling: boolean;
  fileType: UploadFileTypeEnum | null;
  isReUpload: boolean;
  schemasLoading: boolean;
  schemasError?: Error;
};

export type CreateVisitAction = PayloadAction<{
  data: CreateVisit;
  selectedClinicalTrial: string;
  selectedSite: string;
}>;
export type CreateVisitSuccessAction = PayloadAction<{
  data: VisitResponse;
}>;

export type UploadFileAction = PayloadAction<{
  file: File;
  selectedClinicalTrial: string;
  selectedSite: string;
  additionalComment?: string;
}>;

export type UploadFileSuccessAction = PayloadAction<{
  status: UploadStatus;
  shouldContinuePolling: boolean;
}>;

export type GetPolingStatusAction = PayloadAction<{
  selectedClinicalTrial: string;
  selectedSite: string;
}>;

export type GetPolingStatusCheckAction = PayloadAction<PolingResponse>;

export type GetPolingStatusSuccessAction = PayloadAction<{
  status: UploadStatus;
  pollingData: PolingResponse;
}>;

export type UpdateEDTFSchemasAction = PayloadAction<FormState>;

export type SubmitUploadAction = PayloadAction<{
  selectedClinicalTrial: string;
  selectedSite: string;
  verificationStepsData: Partial<VerificationStepsItem>[];
}>;

export type SetStatusAction = PayloadAction<UploadStatus>;

export type UpdateEDTFDataAction = PayloadAction<FormState | null>;
export type UpdateEDTFDataSuccessAction = PayloadAction<{
  signedUrlResponse: { signedUrl: string };
}>;

export type CancelUploadAction = PayloadAction<{
  selectedClinicalTrial: string;
  selectedSite: string;
}>;

export type GetEDTFSchemasSuccessAction = PayloadAction<UploadFormData[]>;
